// Ported from genieos/web/src/components/gamma/Input.tsx @ 84923d036
import { forwardRef } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { InputProps, InputVariant, InputSize } from './types';

const variantStyles: Record<InputVariant, string> = {
  default: 'border-ems-border focus:border-ems-border-bright focus:ring-ems-green/20',
  error: 'border-ems-red focus:border-ems-red focus:ring-ems-red/20',
  success: 'border-ems-green/40 focus:border-ems-green focus:ring-ems-green/20',
  warning: 'border-ems-amber/40 focus:border-ems-amber focus:ring-ems-amber/20',
};

const sizeStyles: Record<InputSize, string> = {
  sm: 'px-3 py-1.5 text-sm min-h-[36px]',
  md: 'px-3 py-2 text-sm min-h-[44px]',
  lg: 'px-4 py-3 text-base min-h-[52px]',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  variant = 'default',
  size = 'md',
  leftIcon,
  rightIcon,
  helperText,
  help,
  required = false,
  className,
  id,
  ...props
}, ref) => {
  const finalHelperText = helperText || help;
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const hasError = !!error;
  const actualVariant = hasError ? 'error' : variant;

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-ems-text-secondary mb-2">
          {label}
          {required && <span className="text-ems-red ml-1" aria-label="required">*</span>}
        </label>
      )}

      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ems-text-secondary">
            {leftIcon}
          </div>
        )}

        <input
          ref={ref}
          id={inputId}
          className={cn(
            'w-full bg-ems-bg border rounded-lg',
            'text-ems-text-primary font-mono',
            'focus:outline-none focus:ring-1',
            'disabled:opacity-40 disabled:cursor-not-allowed',
            'placeholder:text-ems-text-muted',
            'transition-colors',
            variantStyles[actualVariant],
            sizeStyles[size],
            leftIcon && 'pl-10',
            (rightIcon || hasError) && 'pr-10',
            className
          )}
          aria-invalid={hasError ? 'true' : 'false'}
          aria-describedby={
            error ? `${inputId}-error` :
            finalHelperText ? `${inputId}-helper` :
            undefined
          }
          {...props}
        />

        {(rightIcon || hasError) && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {hasError ? (
              <AlertCircle className="w-4 h-4 text-ems-red" />
            ) : rightIcon ? (
              <div className="text-ems-text-secondary">{rightIcon}</div>
            ) : null}
          </div>
        )}
      </div>

      {error && (
        <p id={`${inputId}-error`} className="mt-1 text-sm text-ems-red" role="alert">
          {error}
        </p>
      )}

      {finalHelperText && !error && (
        <p id={`${inputId}-helper`} className="mt-1 text-sm text-ems-text-secondary">
          {finalHelperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';
